import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  TrendingUp,
  TrendingDown,
  Receipt,
  Eye,
  ChevronRight,
  PieChart,
  Euro,
  Percent,
  Users,
  Calendar
} from "lucide-react";
import { 
  ResponsiveContainer, 
  PieChart as RechartsPie, 
  Pie, 
  Cell, 
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const COLORS = ['#007AFF', '#34C759', '#FF9500', '#AF52DE', '#FF2D55', '#5856D6', '#00C7BE'];

const SERVICE_TYPES = [
  "Completo",
  "Cremazione",
  "Economico",
  "Premium",
  "Base"
];

const STATUS_OPTIONS = [
  { value: "in_corso", label: "In Corso", color: "bg-[#FF9500]" },
  { value: "completato", label: "Completato", color: "bg-[#34C759]" },
  { value: "annullato", label: "Annullato", color: "bg-[#FF3B30]" }
];

const ServiziFunerari = () => {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceDetail, setServiceDetail] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    deceased_name: "",
    service_date: new Date().toISOString().split("T")[0],
    client_name: "",
    service_type: "Completo",
    revenue: "",
    notes: "",
    status: "in_corso"
  });

  useEffect(() => {
    fetchServices();
    fetchSummary();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/funeral-services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento servizi");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/funeral-services/stats/summary`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const fetchServiceDetail = async (serviceId) => {
    try {
      const response = await axios.get(`${API}/funeral-services/${serviceId}`, getAuthHeaders());
      setServiceDetail(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento dettagli");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        revenue: parseFloat(formData.revenue) || 0
      };

      if (editingService) {
        await axios.put(`${API}/funeral-services/${editingService.id}`, data, getAuthHeaders());
        toast.success("Servizio aggiornato");
      } else {
        await axios.post(`${API}/funeral-services`, data, getAuthHeaders());
        toast.success("Servizio aggiunto");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchServices();
      fetchSummary();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo servizio? I costi collegati verranno scollegati.")) return;
    try {
      await axios.delete(`${API}/funeral-services/${id}`, getAuthHeaders());
      toast.success("Servizio eliminato");
      fetchServices();
      fetchSummary();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      deceased_name: service.deceased_name,
      service_date: service.service_date,
      client_name: service.client_name || "",
      service_type: service.service_type,
      revenue: service.revenue.toString(),
      notes: service.notes || "",
      status: service.status
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      deceased_name: "",
      service_date: new Date().toISOString().split("T")[0],
      client_name: "",
      service_type: "Completo",
      revenue: "",
      notes: "",
      status: "in_corso"
    });
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.deceased_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (service.client_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || service.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <Badge className={`${statusConfig.color} text-white`}>
        {statusConfig.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Chart data for costs by category
  const categoryChartData = summary?.costs_by_category 
    ? Object.entries(summary.costs_by_category).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="animate-fade-in" data-testid="servizi-funerari-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Servizi Funerari</h1>
          <p className="page-subtitle">Gestione funerali e analisi costi/ricavi</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full px-6"
              data-testid="add-service-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Servizio
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">
                {editingService ? "Modifica Servizio" : "Nuovo Servizio Funebre"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Nome Defunto</Label>
                <Input
                  value={formData.deceased_name}
                  onChange={(e) => setFormData({ ...formData, deceased_name: e.target.value })}
                  placeholder="Nome e Cognome"
                  className="h-12 rounded-xl border-[#D2D2D7]"
                  data-testid="service-deceased-name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Data Servizio</Label>
                  <Input
                    type="date"
                    value={formData.service_date}
                    onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="service-date"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Tipo Servizio</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="service-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                      {SERVICE_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Cliente</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Nome cliente"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="service-client"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Ricavo Totale (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.revenue}
                    onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                    placeholder="0,00"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="service-revenue"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Stato</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="service-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Note</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="h-12 rounded-xl border-[#D2D2D7]"
                  data-testid="service-notes"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 h-12 rounded-full border-[#D2D2D7]"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
                  data-testid="save-service-btn"
                >
                  {editingService ? "Aggiorna" : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Servizi Totali</p>
                  <p className="text-xl font-semibold text-[#1D1D1F]">{summary.total_services}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#34C759]/10 rounded-xl flex items-center justify-center">
                  <Euro className="w-5 h-5 text-[#34C759]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Ricavo Medio</p>
                  <p className="text-xl font-semibold text-[#34C759]">{formatCurrency(summary.avg_revenue_per_service)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF9500]/10 rounded-xl flex items-center justify-center">
                  <Percent className="w-5 h-5 text-[#FF9500]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">% Costi Media</p>
                  <p className="text-xl font-semibold text-[#FF9500]">{summary.avg_cost_percentage}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#AF52DE]/10 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#AF52DE]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Tasso Profittabilità</p>
                  <p className="text-xl font-semibold text-[#AF52DE]">{summary.profitability_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868B]" />
              <Input
                placeholder="Cerca servizi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-[#D2D2D7]"
                data-testid="search-services"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 h-11 rounded-xl border-[#D2D2D7]" data-testid="filter-status">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardContent className="p-0">
          <Table data-testid="services-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Servizio
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Data
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Stato
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Ricavo
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Costi
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-center">
                  % Costi
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Margine
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Azioni
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServices.length > 0 ? (
                filteredServices.map((service) => (
                  <TableRow key={service.id} className="hover:bg-[#F5F5F7]/30">
                    <TableCell className="px-6 py-4">
                      <div>
                        <p className="font-medium text-[#1D1D1F]">{service.deceased_name}</p>
                        <p className="text-sm text-[#86868B]">
                          {service.client_name && `Cliente: ${service.client_name} • `}
                          {service.service_type}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-[#86868B]">
                      {new Date(service.service_date).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {getStatusBadge(service.status)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right font-semibold text-[#34C759]">
                      {formatCurrency(service.revenue)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right font-semibold text-[#FF3B30]">
                      {formatCurrency(service.total_costs)}
                      <p className="text-xs text-[#86868B] font-normal">{service.costs_count} voci</p>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`font-semibold ${service.cost_percentage > 70 ? 'text-[#FF3B30]' : service.cost_percentage > 50 ? 'text-[#FF9500]' : 'text-[#34C759]'}`}>
                          {service.cost_percentage}%
                        </span>
                        <Progress 
                          value={service.cost_percentage} 
                          className="w-16 h-2"
                          style={{
                            '--progress-color': service.cost_percentage > 70 ? '#FF3B30' : service.cost_percentage > 50 ? '#FF9500' : '#34C759'
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell className={`px-6 py-4 text-right font-semibold ${service.is_profitable ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                      {formatCurrency(service.margin)}
                      <p className="text-xs text-[#86868B] font-normal">{service.margin_percentage}%</p>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fetchServiceDetail(service.id)}
                          className="h-9 w-9 rounded-lg hover:bg-[#007AFF]/10"
                          data-testid={`view-service-${service.id}`}
                        >
                          <Eye className="w-4 h-4 text-[#007AFF]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(service)}
                          className="h-9 w-9 rounded-lg hover:bg-[#FF9500]/10"
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Pencil className="w-4 h-4 text-[#FF9500]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(service.id)}
                          className="h-9 w-9 rounded-lg hover:bg-[#FF3B30]/10"
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-[#86868B]">
                    Nessun servizio trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl max-w-3xl max-h-[85vh] overflow-y-auto">
          {serviceDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-[#1D1D1F] flex items-center justify-between">
                  <span>{serviceDetail.deceased_name}</span>
                  {getStatusBadge(serviceDetail.status)}
                </DialogTitle>
                <DialogDescription>
                  {serviceDetail.service_type} • {new Date(serviceDetail.service_date).toLocaleDateString('it-IT')}
                  {serviceDetail.client_name && ` • Cliente: ${serviceDetail.client_name}`}
                </DialogDescription>
              </DialogHeader>
              
              {/* KPI Row */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="p-4 bg-[#34C759]/10 rounded-xl text-center">
                  <p className="text-xs text-[#86868B] uppercase">Ricavo</p>
                  <p className="text-lg font-bold text-[#34C759]">{formatCurrency(serviceDetail.revenue)}</p>
                </div>
                <div className="p-4 bg-[#FF3B30]/10 rounded-xl text-center">
                  <p className="text-xs text-[#86868B] uppercase">Costi Totali</p>
                  <p className="text-lg font-bold text-[#FF3B30]">{formatCurrency(serviceDetail.total_costs)}</p>
                </div>
                <div className={`p-4 ${serviceDetail.is_profitable ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'} rounded-xl text-center`}>
                  <p className="text-xs text-[#86868B] uppercase">Margine</p>
                  <p className={`text-lg font-bold ${serviceDetail.is_profitable ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(serviceDetail.margin)}
                  </p>
                </div>
                <div className="p-4 bg-[#007AFF]/10 rounded-xl text-center">
                  <p className="text-xs text-[#86868B] uppercase">% Costi</p>
                  <p className="text-lg font-bold text-[#007AFF]">{serviceDetail.cost_percentage}%</p>
                </div>
              </div>

              {/* Costs by Category */}
              {serviceDetail.costs_by_category && Object.keys(serviceDetail.costs_by_category).length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-3">Costi per Categoria</h3>
                  <div className="space-y-3">
                    {Object.entries(serviceDetail.costs_by_category).map(([category, data], index) => (
                      <div key={category} className="p-4 bg-[#F5F5F7] rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="font-medium text-[#1D1D1F]">{category}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-[#1D1D1F]">{formatCurrency(data.amount)}</span>
                            <span className="text-sm text-[#86868B] ml-2">
                              ({data.percentage_of_revenue}% del ricavo)
                            </span>
                          </div>
                        </div>
                        <Progress 
                          value={data.percentage_of_revenue} 
                          className="h-2"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Costs List */}
              {serviceDetail.costs && serviceDetail.costs.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-3">Dettaglio Costi ({serviceDetail.costs.length})</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {serviceDetail.costs.map((cost) => (
                      <div key={cost.id} className="flex items-center justify-between p-3 bg-[#F5F5F7]/50 rounded-xl">
                        <div>
                          <p className="font-medium text-[#1D1D1F]">{cost.description}</p>
                          <p className="text-sm text-[#86868B]">
                            {cost.category} • {new Date(cost.date).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#FF3B30]">{formatCurrency(cost.amount)}</p>
                          <p className="text-xs text-[#86868B]">
                            {cost.percentage_of_revenue}% del ricavo
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {serviceDetail.costs && serviceDetail.costs.length === 0 && (
                <div className="mt-6 text-center py-8 bg-[#F5F5F7] rounded-xl">
                  <Receipt className="w-12 h-12 text-[#86868B] mx-auto mb-3" />
                  <p className="text-[#86868B]">Nessun costo registrato per questo servizio</p>
                  <p className="text-sm text-[#86868B] mt-1">
                    Vai alla pagina Costi Funerali e collega i costi a questo servizio
                  </p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiziFunerari;
